import { apiRequest, ApiError } from "@/lib/api/client";
import { getAuthToken } from "@/lib/api/token-store";

function createAuthHeaders(token, headers = {}) {
  const normalizedToken = String(token || getAuthToken() || "").trim();
  if (!normalizedToken) {
    throw new ApiError("authorization token is required", 401);
  }
  return {
    Authorization: `Bearer ${normalizedToken}`,
    ...headers,
  };
}

/**
 * Fetch the caller's account-bound client settings.
 * GET /api/users/me/settings → { settings: { key: value } }
 *
 * The explicit token/signal params exist for the sync layer: the login preload runs BEFORE
 * setAuthSession (no active token yet) and profile-switch flushes run with the OUTGOING
 * user's stashed token.
 */
export async function fetchMySettings({ token, signal } = {}) {
  const payload = await apiRequest("/api/users/me/settings", {
    method: "GET",
    headers: createAuthHeaders(token),
    signal,
  });
  return payload?.settings && typeof payload.settings === "object" ? payload.settings : {};
}

/**
 * Save account-bound client settings — partial by key, full replace within a key.
 * PUT /api/users/me/settings { settings: { key: value } } → { settings: { key: value } }
 */
export async function putMySettings({ settings, token } = {}) {
  if (!settings || typeof settings !== "object" || Object.keys(settings).length === 0) {
    throw new ApiError("settings are required", 400);
  }
  const payload = await apiRequest("/api/users/me/settings", {
    method: "PUT",
    headers: createAuthHeaders(token, { "Content-Type": "application/json" }),
    body: JSON.stringify({ settings }),
  });
  return payload?.settings && typeof payload.settings === "object" ? payload.settings : {};
}
